import { range } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { BouColor } from '../../../../../../generic/global/bou-colors';
import { useImpactChartContext, useImpactChartHeights, useImpactChartWidths } from '../context';
import { grayBorder } from '../shared';
import { DimensionRowLeftPart } from './dimension-row/left-part';
import { getBorderBottom, Root } from './index';
import { DimensionBlockProps } from './types';

interface LoadingSkeletonProps extends DimensionBlockProps {
  svgId: string;
}
export function DimensionBlockLoadingSkeleton(props: LoadingSkeletonProps) {
  const { dimension } = props;
  const hasFinishedLoading = useImpactChartContext().hasFinishedLoading;
  const heights = useImpactChartHeights();
  const widths = useImpactChartWidths();
  const desktopMode = useImpactChartContext().desktopMode;

  const seriesCount = useImpactChartContext().data.length;
  const seriesRange = range(0, seriesCount || 1);

  const barHeight = heights.dimensionRowBarHeight;

  // OA: This is a highly questionable way to match the sizing in the svg.
  // It *seems* to work well enough, but may need some revision.
  const scoreColumnLeft = widths.title + widths.bars + widths.middle / 2 - 21;

  const centerColumnWidth = '2.7em';
  const borderColumnWidth = '10em';
  const horizontalPadding = '1.2em';

  return (
    <Root
      style={{
        height: heights.dimensionRow,
        borderBottom: getBorderBottom(dimension),
      }}
    >
      <div
        style={{
          marginLeft: widths.commonHorizontalPadding,
          marginRight: widths.commonHorizontalPadding,
          borderTop: `1px solid ${grayBorder}`,
        }}
      />
      <TitlesAndSkeletonWrap>
        <svg width={widths.title} height={heights.dimensionRow}>
          <rect x={0} y={0} width={widths.title} height={heights.dimensionRow} fill="transparent" />
          <DimensionRowLeftPart {...props} />
        </svg>
        {seriesRange.map(index => {
          const position = index * (barHeight + 4);
          const verticalPadding = barHeight / 2;
          const top = `${position + verticalPadding}px`;
          return (
            <SkeletonWrap
              key={index}
              style={{
                left: 0,
              }}
            >
              {desktopMode && !hasFinishedLoading && (
                <StyledSkeleton
                  width={borderColumnWidth}
                  height={barHeight}
                  style={{
                    left: `calc(${scoreColumnLeft}px - ${horizontalPadding} - ${borderColumnWidth})`,
                    top,
                  }}
                />
              )}
              {!hasFinishedLoading ? (
                <StyledSkeleton
                  width={centerColumnWidth}
                  height={barHeight}
                  style={{
                    left: scoreColumnLeft,
                    top,
                  }}
                />
              ) : (
                <NotAvailableText
                  style={{
                    left: scoreColumnLeft,
                    top,
                    width: centerColumnWidth,
                  }}
                >
                  N/A
                </NotAvailableText>
              )}
              {desktopMode && !hasFinishedLoading && (
                <StyledSkeleton
                  width={borderColumnWidth}
                  height={barHeight}
                  style={{
                    left: `calc(${scoreColumnLeft}px + ${centerColumnWidth} + ${horizontalPadding})`,
                    top,
                  }}
                />
              )}
            </SkeletonWrap>
          );
        })}
      </TitlesAndSkeletonWrap>
    </Root>
  );
}
const TitlesAndSkeletonWrap = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;
const SkeletonWrap = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
`;
const StyledSkeleton = styled(Skeleton)`
  position: absolute;
  margin-top: 0.5em;
`;

const NotAvailableText = styled.span`
  position: absolute;
  margin-top: 0.5em;
  text-align: center;
  color: ${BouColor.TOOL_TEXT_DISABLED_GRAY};
`;
